import { ExecutorGroup } from '../../common/model/executor-group';
import { DocumentInformationNoteModel } from '../../core/model/information-note';
import { ModelBase } from '../../core/model/model-base';
import { PostTargetModel } from '../service/base/post-target-loader.interface';
import { Area } from './area';
import { Building } from './building';
import { PropertyAppendix } from './property-appendix';
import { PropertyDocument } from './property-document';
import { WebUser } from '../../common/model/webUser';
import { OrganizationOfTenantsKind, OrganizationOfTenantsKindToString } from './const/enums';
import { PropertyTax } from './property-tax';
import { PropertyTaxValue } from '../component/property-tax-form/property-tax-form.component';
import { LinkedCustomProperty } from './linked-custom-property';
import { LocalDate } from '../../core/model/local-date';

export class Property extends ModelBase {
  constructor(dataContext?: any) {
    super();
    if (dataContext) {
      this.buildings = Array.isArray(dataContext.buildings) ? dataContext.buildings.map(x => Object.assign(new Building(), x)) : [];
      this.appendices = Array.isArray(dataContext.appendices)
        ? dataContext.appendices.map(ent => Object.assign(new PropertyAppendix(), ent))
        : [];
      this.areas = Array.isArray(dataContext.areas) ? dataContext.areas.map(ent => Object.assign(new Area(), ent)) : [];
      this.propertyCustomProperties = Array.isArray(dataContext.propertyCustomProperties)
        ? dataContext.propertyCustomProperties.map(ent => Object.assign(new LinkedCustomProperty(), ent))
        : [];
    } else {
      this.buildings = [];
      this.appendices = [];
      this.areas = [];
      this.propertyCustomProperties = [];
    }
  }

  get hasBuildings(): boolean {
    return this.buildings && this.buildings.length > 0;
  }
  buildings: Building[];
  appendices: PropertyAppendix[];
  areas: Area[];
  propertyCustomProperties: LinkedCustomProperty[];
  propertyCustomPropertiesTextValues: Record<string, string>;
  propertyCustomPropertiesDateValues: Record<string, LocalDate>;
  propertyCustomPropertiesNumericValues: Record<string, number>;
  propertyCustomPropertiesBooleanValues: Record<string, boolean>;
  companyBankAccountId: string;
  company: string;
  companyName: string;
  companyNumber: number;
  costPlace: string;
  costBearer: string;
  id: string;
  municipality: string;
  note: string;
  paymentReceiver: string;
  propertyName: string;
  propertyNumber: string;
  specialTermsDwelling: string;
  specialTermsParking: string;
  generalTermsDwelling: string;
  generalTermsParking: string;
  rentPaymentTermsDwelling: string;
  rentPaymentTermsParking: string;
  imdPriceListColdWaterId: string;
  imdPriceListElectricityId: string;
  imdPriceListChargingElectricityId: string;
  imdPriceListHotWaterId: string;
  imdPriceListHeatingId: string;
  hasNegotiationProcedure: boolean;
  coldWaterConsumptionM0: number;
  coldWaterConsumptionM1: number;
  coldWaterConsumptionM2: number;
  coldWaterConsumptionM3: number;
  coldWaterConsumptionM4: number;
  electricityConsumptionM0: number;
  electricityConsumptionM1: number;
  electricityConsumptionM2: number;
  electricityConsumptionM3: number;
  electricityConsumptionM4: number;
  hotWaterConsumptionM0: number;
  hotWaterConsumptionM1: number;
  hotWaterConsumptionM2: number;
  hotWaterConsumptionM3: number;
  hotWaterConsumptionM4: number;
  heatingConsumptionM0: number;
  heatingConsumptionM1: number;
  heatingConsumptionM2: number;
  heatingConsumptionM3: number;
  heatingConsumptionM4: number;
  documents: PropertyDocument[];
  documentInformationNotes: DocumentInformationNoteModel[];

  // Property Taxes
  propertyTaxes: PropertyTax[] = [];
  removedPropertyTaxIds: string[] = [];
  removedBuildingPropertyTaxAssessmentIds: string[] = [];
  propertyTaxValuesByYear: PropertyTaxValue[] = [];

  // Resource
  faultNotificationRecipientId: string;
  faultNotificationRecipientName: string;
  faultNotificationRecipient: WebUser;

  // Fault notification executor group
  faultNotificationExecutorGroupId: string;
  faultNotificationExecutorGroupName: string;
  faultNotificationExecutorGroup: ExecutorGroup;

  // Fault notification executor web user
  faultNotificationExecutorWebUserId: string;
  faultNotificationExecutorWebUserName: string;
  faultNotificationExecutorWebUser: WebUser;

  // Dwelling inspection executor web user
  dwellingInspectionExecutorWebUserId: string;
  dwellingInspectionExecutorWebUserName: string;
  dwellingInspectionExecutorWebUser: WebUser;

  organizationOfTenants: OrganizationOfTenantsKind;
  otherOrganizationOfTenants: string;
  get organizationOfTenantsText(): string {
    return OrganizationOfTenantsKindToString(this.organizationOfTenants);
  }
  get negotiatingClause(): boolean {
    return this.hasNegotiationProcedure;
  }
  set negotiatingClause(value) {
    this.hasNegotiationProcedure = value;
  }
  toString() {
    return `${this.propertyNumber ? this.propertyNumber + ' ' : ''}${this.propertyName}`;
  }

  // Selection items
  executorGroups: ExecutorGroup[] = [];
  executorWebUsers: WebUser[] = [];
  inspectorWebUsers: WebUser[] = [];
}

export class PropertyPostTarget extends ModelBase implements PostTargetModel {
  constructor(dataContext?: PropertyPostTarget) {
    super();
    if (dataContext) {
      this.id = dataContext.id || '';
      this.propertyNumberName = dataContext.propertyNumberName || '';
      this.companyNumberName = dataContext.companyNumberName || '';
    }
  }

  companyNumberName: string;
  propertyNumberName: string;

  postTargetDescription() {
    return {
      key: 'POST.POST_TARGET_DESCRIPTION.PROPERTY',
      params: { companyName: this.companyNumberName, propertyName: this.propertyNumberName },
    };
  }

  toAutocompleteValue(viewType: 'list' | 'field', culture?: string): string {
    switch (viewType) {
      case 'field':
      case 'list':
        return this.propertyNumberName;
    }
  }

  toString(culture?: string): string {
    return this.propertyNumberName;
  }
}
